import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { EuroIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import {
  JourneyBasePriceChangeEnum,
  journeyBasePriceChangeEnumMapping,
  JourneyBasePriceInput,
  JourneyBasePriceTypeEnum,
  journeyBasePriceTypeEnumMapping
} from '../../../lib/types/models/product/product-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<JourneyBasePriceInput> & {
  className?: string
}

const formSchema: ZodType<JourneyBasePriceInput> = z.object({
  value: createNumberSchema('valeur'),
  change: createEnumSchema({
    fieldName: 'Changement de prix',
    enumType: JourneyBasePriceChangeEnum
  }),
  type: createEnumSchema({ fieldName: 'Type de prix', enumType: JourneyBasePriceTypeEnum })
})

export const ProductDatePriceForm = ({
  className,
  defaultValues,
  ...validationButtonProps
}: Props) => {
  const form = useForm<JourneyBasePriceInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })
  const type = form.watch('type')

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-3', className)}>
          <div className={'flex flex-col gap-2'}>
            <Typography variant={'body-medium'}>
              Voulez-vous augmenter ou diminuer le prix ? *
            </Typography>

            <div className={'flex flex-row gap-4'}>
              {Object.values(JourneyBasePriceChangeEnum).map((key) => (
                <FormField
                  key={key}
                  control={form.control}
                  name={'change'}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledRadioInput
                          {...field}
                          label={journeyBasePriceChangeEnumMapping[key]}
                          checked={field.value === key}
                          onCheck={() => field.onChange(key)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>

          <div className={'flex flex-col gap-2'}>
            <Typography variant={'body-medium'}>Ajustement des prix *</Typography>

            <div className={'flex flex-row gap-4'}>
              {Object.values(JourneyBasePriceTypeEnum).map((key) => (
                <FormField
                  key={key}
                  control={form.control}
                  name={'type'}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledRadioInput
                          {...field}
                          label={journeyBasePriceTypeEnumMapping[key]}
                          checked={field.value === key}
                          onCheck={() => field.onChange(key)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ))}
            </div>

            <FormField
              control={form.control}
              name={'value'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      required
                      {...field}
                      type={'number'}
                      label={type === JourneyBasePriceTypeEnum.PERCENTAGE ? 'Pourcentage' : 'Prix'}
                      icon={EuroIcon}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}
